export interface CompressImageOptions {
  maxDimension?: number;
  quality?: number;
}

export interface CompressedImage {
  blob: Blob;
  mimeType: "image/webp" | "image/jpeg";
  width: number;
  height: number;
}

let webpSupportCache: boolean | null = null;

function supportsWebpEncoding(): boolean {
  if (webpSupportCache !== null) return webpSupportCache;
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  webpSupportCache = canvas.toDataURL("image/webp").startsWith("data:image/webp");
  return webpSupportCache;
}

function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  if (width <= max && height <= max) return { width, height };
  const ratio = width > height ? max / width : max / height;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

async function loadBitmap(file: File | Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo leer la imagen"));
    };
    img.src = url;
  });
}

/**
 * Redimensiona (máx. `maxDimension` en el lado más largo, nunca agranda) y
 * recomprime una foto en el navegador antes de subirla, para no depender de
 * procesamiento server-side (el upload corre en Cloudflare Workers, donde
 * librerías nativas como sharp no funcionan). Sale en WebP si el navegador
 * puede codificarlo, si no cae a JPEG.
 */
export async function compressImageFile(
  file: File,
  { maxDimension = 1600, quality = 0.82 }: CompressImageOptions = {},
): Promise<CompressedImage> {
  const bitmap = await loadBitmap(file);
  const { width, height } = fitWithin(bitmap.width, bitmap.height, maxDimension);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo preparar el lienzo de compresión");
  // Sin esto, Chrome usa su filtro de reescalado por defecto ("low"), que
  // conserva más ruido de alta frecuencia y pesa bastante más a igual calidad WebP.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, width, height);
  if ("close" in bitmap) bitmap.close();

  const mimeType = supportsWebpEncoding() ? "image/webp" : "image/jpeg";
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, mimeType, quality),
  );
  if (!blob) throw new Error("No se pudo comprimir la imagen");
  return { blob, mimeType, width, height };
}
